import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import AICostBadge from '../components/AICostBadge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { toast } from 'sonner';
import { 
 Calculator, Plus, Trash2, Home, DollarSign, Zap, 
 Package, Clock, TrendingUp, Save, ChevronDown, ChevronUp,
 AlertCircle, Sparkles, CheckCircle, XCircle, Lightbulb
} from 'lucide-react';
import CalculatorTutorial from '../components/CalculatorTutorial';
import HeadlineMetric from '../components/HeadlineMetric';
import HouseholdLiveInsights from '../components/calculator/HouseholdLiveInsights';
import PageHero from '../components/PageHero';
import ToolSelector from '../components/ToolSelector';

const HouseholdCalculator = () => {
 const navigate = useNavigate();
 const { API } = useContext(AuthContext);
 
 // State
 const [batchName, setBatchName] = useState('');
 const [freezeDryerModels, setFreezeDryerModels] = useState([]);
 const [selectedModel, setSelectedModel] = useState(null);
 const [cycleTimeHours, setCycleTimeHours] = useState('24');
 const [electricityRate, setElectricityRate] = useState('0.12');
 const [foodCategories, setFoodCategories] = useState({});
 const [packagingTypes, setPackagingTypes] = useState({});
 const [items, setItems] = useState([]);
 const [outputs, setOutputs] = useState(null);
 const [loading, setLoading] = useState(false);
 const [aiLoading, setAiLoading] = useState(false);
 const [showOverloadWarning, setShowOverloadWarning] = useState(false);
 const [batchSummary, setBatchSummary] = useState(null);
 const [savedBatches, setSavedBatches] = useState([]);
 const [showLoadModal, setShowLoadModal] = useState(false);
 const [itemValidation, setItemValidation] = useState({}); // { [itemId]: [missing field labels] }
 
 // Batch-level packing costs (not per item)
 const [batchPackingCosts, setBatchPackingCosts] = useState({
 container_cost: '',
 o2_absorber_cost: '',
 cleaning_cost: ''
 });
 
 // Collapsible sections
 const [showBatchBasics, setShowBatchBasics] = useState(true);
 const [showCosts, setShowCosts] = useState(true);
 const [showResults, setShowResults] = useState(true);
 
 // Fetch data on mount
 useEffect(() => {
 const fetchData = async () => {
 try {
 const [modelsRes, categoriesRes, packagingRes] = await Promise.all([
 axios.get(`${API}/calculator/freeze-dryer-models`),
 axios.get(`${API}/household/food-categories`),
 axios.get(`${API}/household/packaging-types`)
 ]);
 
 setFreezeDryerModels(modelsRes.data.models || []);
 setFoodCategories(categoriesRes.data.categories || {});
 setPackagingTypes(packagingRes.data.packaging_types || {});
 } catch (error) {
 console.error('Failed to fetch data:', error);
 toast.error('Failed to load calculator data');
 }
 };
 fetchData();
 }, [API]);
 
 // Fetch saved batches
 useEffect(() => {
 const fetchSavedBatches = async () => {
 try {
 const response = await axios.get(`${API}/household/batch-logs`);
 setSavedBatches(response.data.logs || []);
 } catch (error) {
 console.error('Error fetching saved batches:', error);
 }
 };
 fetchSavedBatches();
 }, [API]);
 
 const addItem = () => {
 const newItem = {
 id: Date.now().toString(),
 food_name: '',
 category: 'fruits',
 wet_weight: '',
 weight_unit: 'lbs',
 cost_per_unit: '',
 cost_unit: 'lbs',
 packaging_type: 'mylar_oxygen',
 wet_weight_per_bag: '',
 bag_weight_unit: 'g',
 custom_moisture: '',
 cycle_time_hours: '24',
 electricity_rate: '0.12'
 };
 setItems([...items, newItem]);
 };
 
 const removeItem = (id) => {
 setItems(items.filter(item => item.id !== id));
 };
 
 const updateItem = (id, field, value) => {
 setItems(items.map(item => 
 item.id === id ? { ...item, [field]: value } : item
 ));
 // Clear this item's inline validation warnings as soon as user edits any field
 setItemValidation(prev => {
 if (!prev[id]) return prev;
 const { [id]: _drop, ...rest } = prev;
 return rest;
 });
 };
 
 // Load batch function
 const loadBatch = (batch) => {
 try {
 setBatchName(batch.inputs.batch_name);
 setSelectedModel(freezeDryerModels.find(m => m.id === batch.inputs.freeze_dryer_model_id));
 setCycleTimeHours(batch.inputs.cycle_time_hours.toString());
 setElectricityRate(batch.inputs.electricity_rate_per_kwh.toString());
 
 // Load batch-level packing costs from first item (since they're all the same)
 if (batch.inputs.items && batch.inputs.items.length > 0) {
 const firstItem = batch.inputs.items[0];
 setBatchPackingCosts({
 container_cost: firstItem.container_cost?.toString() || '',
 o2_absorber_cost: firstItem.o2_absorber_cost?.toString() || '',
 cleaning_cost: firstItem.cleaning_cost?.toString() || ''
 });
 }
 
 // Reconstruct items from saved data
 const loadedItems = batch.inputs.items.map(item => ({
 id: Date.now().toString() + Math.random(),
 food_name: item.food_name,
 category: item.category,
 wet_weight: (item.wet_weight_g / 453.592).toFixed(2), // Convert back to lbs
 weight_unit: 'lbs',
 cost_per_unit: '',
 cost_unit: 'lbs',
 packaging_type: item.packaging_type,
 wet_weight_per_bag: '',
 bag_weight_unit: 'g',
 custom_moisture: item.custom_moisture_percent ? item.custom_moisture_percent.toString() : '',
 cycle_time_hours: batch.inputs.cycle_time_hours.toString(),
 electricity_rate: batch.inputs.electricity_rate_per_kwh.toString()
 }));
 
 setItems(loadedItems);
 setShowLoadModal(false);
 toast.success('Batch loaded successfully!');
 } catch (error) {
 console.error('Load error:', error);
 toast.error('Failed to load batch');
 }
 };
 
 // Convert weight to grams
 const convertToGrams = (weight, unit) => {
 const w = parseFloat(weight) || 0;
 switch(unit) {
 case 'lbs': return w * 453.592;
 case 'kg': return w * 1000;
 case 'oz': return w * 28.3495;
 case 'g': 
 default: return w;
 }
 };
 
 // Calculate total batch weight in lbs for display
 const getTotalBatchWeight = () => {
 let totalG = 0;
 items.forEach(item => {
 totalG += convertToGrams(item.wet_weight, item.weight_unit);
 });
 return (totalG / 453.592).toFixed(2);
 };
 
 // Calculate total water content in batch (lbs)
 const getTotalWaterContent = () => {
 let totalWaterG = 0;
 items.forEach(item => {
 const wetWeightG = convertToGrams(item.wet_weight, item.weight_unit);
 const category = foodCategories[item.category];
 if (!category) return;
 
 const moisturePercent = item.custom_moisture ? parseFloat(item.custom_moisture) : category.moisture_percent;
 const waterG = wetWeightG * (moisturePercent / 100);
 totalWaterG += waterG;
 });
 return (totalWaterG / 453.592).toFixed(2);
 };
 
 // Check weight capacity
 const checkWeightCapacity = () => {
 if (!selectedModel || items.length === 0) return null;
 
 const totalLbs = parseFloat(getTotalBatchWeight());
 const maxLbs = selectedModel.max_batch_weight_lb;
 const percentUsed = (totalLbs / maxLbs * 100);
 
 return {
 isOver: totalLbs > maxLbs,
 isUnder: totalLbs < (maxLbs * 0.3), // Less than 30% capacity
 currentWeight: totalLbs,
 maxWeight: maxLbs,
 percentUsed: percentUsed.toFixed(0),
 remainingCapacity: (maxLbs - totalLbs).toFixed(2)
 };
 };
 
 // Calculate food cost for an item
 const calculateItemCost = (item) => {
 if (!item.wet_weight || !item.cost_per_unit) return 0;
 
 const weightG = convertToGrams(item.wet_weight, item.weight_unit);
 const costPerG = convertToGrams(1, item.cost_unit);
 const costPerGram = parseFloat(item.cost_per_unit) / costPerG;
 
 return (weightG * costPerGram).toFixed(2);
 };
 
 // Convert grams to user's selected unit
 const convertFromGrams = (grams, unit) => {
 switch(unit) {
 case 'lbs': return (grams / 453.592).toFixed(2);
 case 'oz': return (grams / 28.3495).toFixed(2);
 case 'kg': return (grams / 1000).toFixed(2);
 case 'g': return grams.toFixed(0);
 default: return grams.toFixed(2);
 }
 };
 
 // Calculate expected dry weight from wet weight
 const calculateExpectedDryWeight = (item) => {
 if (!item.wet_weight) return null;
 
 const category = foodCategories[item.category];
 if (!category) return null;
 
 const wetWeightG = convertToGrams(item.wet_weight, item.weight_unit);
 const moisturePercent = item.custom_moisture ? parseFloat(item.custom_moisture) : category.moisture_percent;
 const moisture = moisturePercent / 100;
 const dryWeightG = wetWeightG * (1 - moisture);
 const waterRemovedG = wetWeightG * moisture;
 
 return {
 dryWeight: convertFromGrams(dryWeightG, item.weight_unit),
 dryWeightG: dryWeightG,
 waterRemoved: convertFromGrams(waterRemovedG, item.weight_unit),
 waterRemovedG: waterRemovedG,
 moisturePercent: moisturePercent,
 unit: item.weight_unit
 };
 };
 
 // Calculate servings based on wet weight per bag
 const calculateServings = (item) => {
 if (!item.wet_weight || !item.wet_weight_per_bag) return 0;
 
 const totalWetG = convertToGrams(item.wet_weight, item.weight_unit);
 const wetPerBagG = convertToGrams(item.wet_weight_per_bag, item.bag_weight_unit);
 
 if (wetPerBagG <= 0) return 0;
 
 return Math.floor(totalWetG / wetPerBagG);
 };
 
 // Calculate expected dry weight per bag
 const calculateDryWeightPerBag = (item) => {
 if (!item.wet_weight_per_bag) return null;
 
 const category = foodCategories[item.category];
 if (!category) return null;
 
 const moisture = item.custom_moisture ? parseFloat(item.custom_moisture) / 100 : category.moisture_percent / 100;
 const wetPerBagG = convertToGrams(item.wet_weight_per_bag, item.bag_weight_unit);
 const dryPerBagG = wetPerBagG * (1 - moisture);
 
 return {
 dryWeightG: dryPerBagG,
 dryWeightOz: (dryPerBagG / 28.3495).toFixed(1)
 };
 };
 
 // Removed piece calculator function
 
 // Ask AI for moisture estimate
 const askAIForMoisture = async (itemId) => {
 const item = items.find(i => i.id === itemId);
 if (!item.food_name) {
 toast.error('Please enter a food name first');
 return;
 }
 
 setAiLoading(true);
 try {
 const response = await axios.post(`${API}/calculator/ai-estimate`, {
 field_name: 'moisture_content',
 user_prompt: `What is the moisture content percentage of ${item.food_name}? Provide only the numeric percentage value (e.g., 91 for 91%).`,
 context: {},
 form_values: {
 product_description: item.food_name
 },
 use_web_search: true
 });
 
 const moisturePercent = response.data.suggested_value;
 updateItem(itemId, 'custom_moisture', moisturePercent.toString());
 toast.success(`AI suggests ${moisturePercent}% moisture for ${item.food_name}`, { duration: 4000 });
 
 } catch (error) {
 console.error('AI estimate error:', error);
 toast.error('Failed to get AI estimate');
 } finally {
 setAiLoading(false);
 }
 };
 
 // Ask AI for cycle time estimate per item
 const askAIForCycleTime = async (itemId) => {
 const item = items.find(i => i.id === itemId);
 if (!item.food_name || !selectedModel) {
 toast.error('Please enter food name and select a machine first');
 return;
 }
 
 setAiLoading(true);
 try {
 const response = await axios.post(`${API}/calculator/ai-estimate`, {
 field_name: 'cycle_time',
 user_prompt: `What is the typical freeze-drying cycle time in hours for ${item.food_name} using a ${selectedModel.display_name}? Provide only the numeric value in hours (e.g., 24 for 24 hours).`,
 context: {},
 form_values: {
 product_description: item.food_name,
 machine_size: selectedModel.display_name
 },
 use_web_search: true
 });
 
 const cycleTime = response.data.suggested_value;
 updateItem(itemId, 'cycle_time_hours', cycleTime.toString());
 toast.success(`AI suggests ${cycleTime} hours for ${item.food_name} on ${selectedModel.display_name}`, { duration: 4000 });
 
 } catch (error) {
 console.error('AI estimate error:', error);
 toast.error('Failed to get AI estimate');
 } finally {
 setAiLoading(false);
 }
 };
 
 const calculate = async (bypassWarning = false) => {
 // Fatal preconditions only. NOTE: the Calculate button is disabled at items.length===0
 // so the "no items" branch is handled by the button itself.
 if (!selectedModel) {
 toast.error('Please select a freeze dryer model');
 return;
 }

 // Inline per-item validation — collect issues, don't hard-block
 const issues = {};
 const validItems = [];
 items.forEach((item) => {
 const missing = [];
 if (!item.food_name) missing.push('food name');
 if (!item.wet_weight) missing.push('wet weight');
 if (!item.wet_weight_per_bag) missing.push('weight per bag');
 if (!item.cycle_time_hours || parseFloat(item.cycle_time_hours) <= 0) missing.push('cycle time');
 if (missing.length) issues[item.id] = missing;
 // "Valid enough" for calc: needs a name and a positive wet weight
 if (item.food_name && parseFloat(item.wet_weight) > 0) validItems.push(item);
 });
 setItemValidation(issues);

 if (validItems.length === 0) {
 toast.error('Fix the highlighted items — every entry needs at least a food name and a wet weight.');
 return;
 }

 const partial = validItems.length < items.length || Object.keys(issues).length > 0;
 if (partial) {
 toast.info('Calculating with best-guess defaults for missing fields — highlighted items need your review.');
 }

 // Sensible defaults for missing per-item fields
 const resolvedItems = validItems.map((item) => {
 const wetWeight = parseFloat(item.wet_weight) || 0;
 const weightPerBag = parseFloat(item.wet_weight_per_bag) || wetWeight || 1; // 1 bag fallback
 const cycle = parseFloat(item.cycle_time_hours) > 0 ? parseFloat(item.cycle_time_hours) : 24;
 return { ...item, wet_weight_per_bag: weightPerBag, cycle_time_hours: cycle };
 });

 // Use the longest cycle time across resolved items
 const longestCycleTime = Math.max(...resolvedItems.map((i) => parseFloat(i.cycle_time_hours) || 24));

 // Check weight capacity - show warning modal if over (unless bypassed)
 const weightCheck = checkWeightCapacity();
 if (weightCheck && weightCheck.isOver && !bypassWarning) {
 setShowOverloadWarning(true);
 return; // Stop here and show modal
 }

 setLoading(true);
 try {
 const inputs = {
 batch_name: batchName || 'Unnamed Batch',
 freeze_dryer_model_id: selectedModel.id,
 cycle_time_hours: longestCycleTime,
 electricity_rate_per_kwh: parseFloat(resolvedItems[0]?.electricity_rate || '0.12'),
 items: resolvedItems.map(item => {
 const servings = calculateServings(item);
 return {
 food_name: item.food_name,
 category: item.category,
 wet_weight_g: convertToGrams(item.wet_weight, item.weight_unit),
 food_cost: parseFloat(calculateItemCost(item)) || 0,
 servings: servings > 0 ? servings : 1,
 packaging_type: item.packaging_type,
 store_price_per_kg: null,
 custom_moisture_percent: item.custom_moisture ? parseFloat(item.custom_moisture) : null,
 container_cost: parseFloat(batchPackingCosts.container_cost) || 0,
 o2_absorber_cost: parseFloat(batchPackingCosts.o2_absorber_cost) || 0,
 cleaning_cost: parseFloat(batchPackingCosts.cleaning_cost) || 0
 };
 })
 };

 const response = await axios.post(`${API}/household/calculate`, inputs);
 setOutputs(response.data.outputs);
 setShowResults(true);
 if (!partial) toast.success('Batch calculated successfully!');

 setTimeout(() => {
 document.getElementById('results-section')?.scrollIntoView({ behavior: 'smooth' });
 }, 100);

 } catch (error) {
 console.error('Calculation error:', error);
 toast.error('Failed to calculate batch');
 } finally {
 setLoading(false);
 }
 };
 
 // Generate AI batch summary with recommendations
 // Format AI analysis by parsing markdown-style text
 const formatAIAnalysis = (text) => {
 if (!text) return null;
 
 // Split by lines
 const lines = text.split('\n');
 const formatted = [];
 
 lines.forEach((line, index) => {
 const trimmed = line.trim();
 
 // Skip empty lines
 if (!trimmed) {
 formatted.push(React.createElement('div', { key: `space-${index}`, className: 'h-3' }));
 return;
 }
 
 // Main headers (## or ###)
 if (trimmed.startsWith('###')) {
 const headerText = trimmed.replace(/^###\s*/, '').replace(/\*\*/g, '');
 formatted.push(
 React.createElement('h4', { 
 key: index, 
 className: 'text-base font-bold text-primary dark:text-primary mt-4 mb-2' 
 }, headerText)
 );
 } else if (trimmed.startsWith('##')) {
 const headerText = trimmed.replace(/^##\s*/, '').replace(/\*\*/g, '');
 formatted.push(
 React.createElement('h3', { 
 key: index, 
 className: 'text-lg font-bold text-primary dark:text-primary mt-5 mb-3 border-b-2 border-primary/20 pb-2' 
 }, headerText)
 );
 }
 // Bullet points
 else if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
 const bulletText = trimmed.substring(2).replace(/\*\*/g, '');
 formatted.push(
 React.createElement('div', { 
 key: index, 
 className: 'flex gap-2 mb-2 ml-4' 
 }, [
 React.createElement('span', { key: 'bullet', className: 'text-primary font-bold mt-1' }, '•'),
 React.createElement('span', { key: 'text', className: 'text-sm text-slate-700 dark:text-slate-300' }, bulletText)
 ])
 );
 }
 // Bold text handling
 else if (trimmed.includes('**')) {
 const parts = trimmed.split('**');
 const content = parts.map((part, i) => 
 i % 2 === 1 
 ? React.createElement('strong', { key: i, className: 'font-bold text-slate-900 dark:text-slate-100' }, part)
 : part
 );
 formatted.push(
 React.createElement('p', { 
 key: index, 
 className: 'text-sm text-slate-700 dark:text-slate-300 mb-2' 
 }, content)
 );
 }
 // Regular paragraph
 else {
 formatted.push(
 React.createElement('p', { 
 key: index, 
 className: 'text-sm text-slate-700 dark:text-slate-300 mb-2' 
 }, trimmed)
 );
 }
 });
 
 return formatted;
 };

 const generateBatchSummary = async () => {
 if (!outputs || items.length === 0) return;
 
 setAiLoading(true);
 try {
 const foodList = items.map(i => {
 const moisturePercent = i.custom_moisture || foodCategories[i.category]?.moisture_percent || '85';
 return `- ${i.food_name} (${foodCategories[i.category]?.name || i.category}, ${moisturePercent}% moisture)`;
 }).join('\n');
 
 const weightCheck = checkWeightCapacity();
 const remainingCapacity = weightCheck ? (weightCheck.maxWeight - weightCheck.currentWeight).toFixed(2) : 0;
 
 // Determine food categories in batch
 const categories = [...new Set(items.map(i => foodCategories[i.category]?.name || i.category))];
 const hasProtein = categories.some(c => c.includes('Meat'));
 const hasProduce = categories.some(c => c.includes('Fruit') || c.includes('Vegetable'));
 const hasDairy = categories.some(c => c.includes('Dairy'));
 
 // Create detailed prompt for AI analysis
 const analysisPrompt = `You are an expert freeze-drying consultant with 15+ years experience. Provide highly specific, actionable recommendations.

═══════════════════════════════════════════════════
CURRENT BATCH ANALYSIS REQUEST
═══════════════════════════════════════════════════

BATCH CONTENTS:
${foodList}

CATEGORIES PRESENT: ${categories.join(', ')}

MACHINE SPECIFICATIONS:
• Model: ${selectedModel?.display_name}
• Total Capacity: ${weightCheck?.maxWeight} lbs
• Current Load: ${weightCheck?.currentWeight} lbs
• AVAILABLE SPACE: ${remainingCapacity} lbs ← FOCUS HERE

═══════════════════════════════════════════════════
REQUIRED ANALYSIS (BE VERY SPECIFIC):
═══════════════════════════════════════════════════

1. BATCH SUMMARY
Write 3-4 sentences analyzing the current batch composition, moisture levels, and freeze-drying characteristics.

2. FOOD SAFETY & COMPATIBILITY
CRITICAL RULES:
• Raw meat + dairy = NEVER (cross-contamination)
• Raw meat + produce = NEVER (pathogen transfer)
• Cooked meat + produce = ACCEPTABLE if properly handled
• Fruits + vegetables = ALWAYS SAFE
• Dairy alone or with other dairy = SAFE

Analyze current batch for violations. Be explicit about risks.

3. RECOMMENDATIONS TO FILL ${remainingCapacity} LBS
THIS IS THE MOST IMPORTANT SECTION - BE VERY SPECIFIC!

${hasProtein && !hasProduce && !hasDairy ? `
PROTEIN BATCH DETECTED - Suggest ONLY other proteins:
• List 5-8 specific protein options (chicken, turkey, lean beef, pork, fish, shrimp)
• Include moisture % for each (usually 60-75%)
• Suggest exact weights (e.g., "2 lbs chicken breast")
• Mix cooked and raw if batch allows
` : ''}

${hasProduce && !hasProtein ? `
PRODUCE BATCH DETECTED - Suggest fruits/vegetables:
• List 6-10 specific produce options with similar moisture
• Include moisture % for each (usually 80-95%)
• Suggest exact weights (e.g., "3 lbs sliced apples")
• Consider seasonal availability
• Mix fruits and vegetables freely
` : ''}

${hasDairy ? `
DAIRY BATCH DETECTED - Suggest only dairy:
• Cheese varieties, milk products, yogurt
• Note: Dairy has shorter shelf life (1-2 years max)
` : ''}

FORMAT EACH SUGGESTION:
✓ [Food name] ([moisture %]) - [suggested weight] lbs
 → [One sentence about why this works]

GIVE AT LEAST 5 SPECIFIC SUGGESTIONS WITH WEIGHTS THAT ADD UP TO ~${remainingCapacity} LBS

4. PROCESSING TIPS FOR THIS COMBINATION
• Pre-treatment recommendations (blanching, pre-freezing, etc.)
• Expected cycle time range
• Cutting/prep uniformity tips
• Storage recommendations
• Shelf life expectations

═══════════════════════════════════════════════════
BE DETAILED, SPECIFIC, AND ACTIONABLE. Include actual food names and weights!`;

 // Call backend AI endpoint
 const response = await axios.post(`${API}/calculator/ai-analysis`, {
 prompt: analysisPrompt,
 context: {
 foods: foodList,
 machine: selectedModel?.display_name,
 remaining_capacity: remainingCapacity
 }
 });
 
 const summary = response.data.analysis || response.data.suggested_value || response.data;
 
 // Check if we got a useful response (not just a number)
 if (typeof summary === 'string' && summary.length > 50) {
 setBatchSummary(summary);
 toast.success('AI batch analysis complete!');
 } else {
 // Fallback: create basic summary
 const fallbackSummary = `BATCH SUMMARY:\nYour batch contains ${items.length} item(s) with various moisture levels. Current load is ${weightCheck?.currentWeight} lbs out of ${weightCheck?.maxWeight} lbs capacity.\n\nREMAINING SPACE: ${remainingCapacity} lbs available\n\nRECOMMENDATIONS:\nConsider adding items with similar moisture content to maximize efficiency.\n\nPROCESSING TIPS:\nEnsure items are cut to similar sizes for even drying.`;
 setBatchSummary(fallbackSummary);
 toast.success('Basic analysis generated');
 }
 
 } catch (error) {
 console.error('AI summary error:', error);
 // Create fallback summary on error
 const weightCheck = checkWeightCapacity();
 const remainingCapacity = weightCheck ? (weightCheck.maxWeight - weightCheck.currentWeight).toFixed(2) : 0;
 const fallbackSummary = `BATCH SUMMARY:\nYour batch contains ${items.length} item(s). Current load is ${weightCheck?.currentWeight || 0} lbs.\n\nREMAINING CAPACITY: ${remainingCapacity} lbs\n\nRECOMMENDATIONS:\nAdd foods with similar moisture content to fill remaining space.\n\nSAFETY: Avoid mixing raw meat with dairy or produce.`;
 setBatchSummary(fallbackSummary);
 toast.warning('Using basic analysis (AI unavailable)');
 } finally {
 setAiLoading(false);
 }
 };
 
 const saveBatch = async () => {
 if (!outputs) {
 toast.error('Please calculate the batch first');
 return;
 }
 
 try {
 const batchData = {
 batch_name: batchName || 'Unnamed Batch',
 inputs: {
 batch_name: batchName || 'Unnamed Batch',
 freeze_dryer_model_id: selectedModel?.id || null,
 cycle_time_hours: parseFloat(cycleTimeHours),
 electricity_rate_per_kwh: parseFloat(electricityRate),
 items: items.map(item => {
 const servings = calculateServings(item);
 return {
 food_name: item.food_name,
 category: item.category,
 wet_weight_g: convertToGrams(item.wet_weight, item.weight_unit),
 food_cost: parseFloat(calculateItemCost(item)),
 servings: servings > 0 ? servings : 1,
 packaging_type: item.packaging_type,
 store_price_per_kg: null
 };
 })
 },
 outputs: outputs
 };
 
 await axios.post(`${API}/household/save-batch`, batchData);
 toast.success('Batch saved successfully!');
 } catch (error) {
 console.error('Save error:', error);
 toast.error('Failed to save batch');
 }
 };

 const weightCheck = checkWeightCapacity();

 return (
 <>
 {/* Print-specific styles */}
 <style>{`
 @media print {
 /* Hide navigation and ads when printing */
 button:not(.print-include),
 .no-print {
 display: none !important;
 }
 
 /* Show legal disclaimer prominently on print */
 .print-disclaimer {
 display: block !important;
 page-break-before: avoid;
 border: 2px solid #000;
 padding: 1rem;
 margin-top: 1rem;
 }
 
 /* Ensure good page breaks */
 .print-section {
 page-break-inside: avoid;
 }
 }
 `}</style>
 
 <div className="min-h-screen bg-background dark:from-neutral-900 dark:to-neutral-800">
 <ToolSelector />
 <PageHero
 eyebrow="Home batch"
 eyebrowIcon={Home}
 title={<>Household <span className="text-amber-brand">Calculator</span></>}
 description="Estimate cost, water loss and shelf life for a home batch — no business math."
 backTo="/calculators"
 backLabel="Calculators"
 testId="household-calc-hero"
 />
 <div className="container mx-auto px-4 py-8 max-w-7xl lg:pr-[22rem] relative">
 {/* Tutorial */}
 <CalculatorTutorial
 storageKey="household-calc-v1"
 title="How to plan a home batch"
 steps={[
 { title: '1 · Pick your machine', body: 'Choose the freeze dryer model you use. Tray count + tray capacity come from that.' },
 { title: '2 · Add each food item', body: 'For each item enter what it is, how much (weight or count), and your cost per unit. The tool converts across grams / oz / lbs / kg for you.' },
 { title: '3 · Watch the tray load', body: 'You will see a live "within capacity / high load / over capacity" hint per item so you can right-size the run.' },
 { title: '4 · Read the summary', body: 'Total cost, projected finished weight, estimated shelf life and packaging suggestions appear at the bottom. Save the batch to reuse the recipe later.' },
 ]}
 />

 {/* Advertisement Banner - Header */}
 <div className="no-print mb-6 rounded-2xl border border-primary/30 bg-primary/5 p-4">
 <div className="flex items-center justify-between gap-4">
 <div className="flex items-center gap-3">
 <div className="w-11 h-11 bg-primary text-primary-foreground rounded-xl flex items-center justify-center flex-shrink-0">
 <Sparkles className="w-5 h-5" />
 </div>
 <div>
 <p className="text-sm font-heading font-extrabold text-foreground">
 Master freeze-drying with our online courses
 </p>
 <p className="text-xs text-muted-foreground">
 Learn from experts, step-by-step tutorials, community support.
 </p>
 </div>
 </div>
 <Button
 size="sm"
 className="rounded-full font-semibold bg-primary text-primary-foreground hover:bg-primary/90 flex-shrink-0"
 onClick={() => navigate('/learning')}
 >
 Explore courses →
 </Button>
 </div>
 </div>

 {/* Live insights snapshot — sticky panel on desktop */}
 <div className="mb-6 lg:hidden">
 <HouseholdLiveInsights items={items} selectedModel={selectedModel} outputs={outputs} />
 </div>
 <div className="hidden lg:block fixed right-6 top-32 w-80 z-10 max-h-[calc(100vh-9rem)] overflow-y-auto no-print">
 <HouseholdLiveInsights items={items} selectedModel={selectedModel} outputs={outputs} />
 </div>

 {/* Batch Name */}
 <Card className="mb-6">
 <CardHeader>
 <CardTitle>Batch Name</CardTitle>
 <CardDescription>Give your batch a memorable name</CardDescription>
 </CardHeader>
 <CardContent>
 <div className="flex gap-2">
 <Input
 value={batchName}
 onChange={(e) => setBatchName(e.target.value)}
 placeholder="e.g., Summer Strawberries 2024"
 className="text-lg flex-1"
 />
 <Button
 type="button"
 onClick={() => {
 const date = new Date();
 const dateStr = `Batch ${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
 setBatchName(dateStr);
 }}
 variant="outline"
 className="whitespace-nowrap"
 >
 <Clock className="w-4 h-4 mr-2" />
 Use Today&apos;s Date
 </Button>
 </div>
 </CardContent>
 </Card>

 {/* Top Action Bar - Save/Load/Print */}
 <Card className="mb-6 bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900">
 <CardContent className="pt-6">
 <div className="flex flex-wrap gap-3">
 <Button
 onClick={saveBatch}
 disabled={!outputs}
 variant="outline"
 className="flex-1 md:flex-none"
 >
 <Save className="w-4 h-4 mr-2" />
 Save Batch
 </Button>
 <Button
 onClick={() => setShowLoadModal(true)}
 variant="outline"
 className="flex-1 md:flex-none"
 >
 <Package className="w-4 h-4 mr-2" />
 Load Batch
 </Button>
 <Button
 onClick={() => window.print()}
 variant="outline"
 className="flex-1 md:flex-none"
 >
 <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
 </svg>
 Print Batch
 </Button>
 <Button
 onClick={() => {
   if (!items || items.length === 0) { toast.error('Add ingredients first'); return; }
   const rows = [['Ingredient', 'Weight', 'Unit', 'Est. cost']];
   items.forEach((it) => {
     rows.push([
       (it.food_type || it.name || 'Unnamed').replace(/,/g, ' '),
       String(it.weight ?? ''),
       (it.weight_unit || 'g'),
       (it.estimated_cost != null ? `$${Number(it.estimated_cost).toFixed(2)}` : ''),
     ]);
   });
   const csv = rows.map((r) => r.join(',')).join('\n');
   const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
   const url = URL.createObjectURL(blob);
   const a = document.createElement('a');
   a.href = url;
   a.download = `fda-shopping-list-${new Date().toISOString().slice(0,10)}.csv`;
   a.click();
   URL.revokeObjectURL(url);
   toast.success('Shopping list downloaded — take it to your favourite grocer!');
 }}
 disabled={!items || items.length === 0}
 variant="outline"
 className="flex-1 md:flex-none"
 data-testid="export-shopping-list-btn"
 >
 <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
 </svg>
 Shopping List
 </Button>
 {outputs && (
 <Button
 onClick={() => {
 generateBatchSummary();
 // Scroll to AI analysis section after generation
 setTimeout(() => {
 document.getElementById('ai-analysis-section')?.scrollIntoView({ 
 behavior: 'smooth',
 block: 'start'
 });
 }, 500);
 }}
 disabled={aiLoading}
 className="flex-1 md:flex-none bg-primary text-primary-foreground hover:bg-primary/90"
 >
 <Sparkles className="w-4 h-4 mr-2" />
 {aiLoading ? 'Analyzing...' : 'AI Batch Analysis'}
 </Button>
 )}
 </div>
 </CardContent>
 </Card>

 {/* Batch Basics (Machine + Cycle + Items) */}
 <Card className="mb-6">
 <CardHeader
 className="cursor-pointer"
 onClick={() => setShowBatchBasics(!showBatchBasics)}
 >
 <div className="flex items-center justify-between">
 <div>
 <CardTitle>Batch Basics</CardTitle>
 <CardDescription>Machine selection, cycle settings, and food items</CardDescription>
 </div>
 {showBatchBasics ? <ChevronUp /> : <ChevronDown />}
 </div>
 </CardHeader>
 {showBatchBasics && (
 <CardContent className="space-y-6">
 {/* Machine Selection */}
 <div className="bg-pro-soft p-4 rounded-lg">
 <p className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-3">🔥 Select Your Freeze Dryer</p>
 
 <div className="grid md:grid-cols-2 gap-3">
 {freezeDryerModels.map(model => (
 <label
 key={model.id}
 className={`flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 transition-all ${
 selectedModel?.id === model.id
 ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 shadow-md'
 : 'border-slate-200 dark:border-slate-700'
 }`}
 >
 <input
 type="radio"
 name="freeze-dryer"
 value={model.id}
 checked={selectedModel?.id === model.id}
 onChange={() => setSelectedModel(model)}
 className="w-4 h-4 mt-1"
 />
 <div className="flex-1">
 <span className="text-sm font-medium block">{model.display_name}</span>
 <span className="text-xs text-muted-foreground block mt-1">
 {model.trays} trays • Max: {model.max_batch_weight_lb} lbs
 </span>
 </div>
 </label>
 ))}
 </div>
 
 {selectedModel && (
 <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-300 dark:border-green-700">
 <p className="text-sm font-semibold text-green-900 dark:text-green-100 mb-2">
 ✓ {selectedModel.display_name} Selected
 </p>
 <div className="grid grid-cols-2 gap-3 text-sm">
 <div>
 <span className="text-muted-foreground">Trays:</span>
 <span className="ml-2 font-bold">{selectedModel.trays}</span>
 </div>
 <div>
 <span className="text-muted-foreground">Max Capacity:</span>
 <span className="ml-2 font-bold text-primary">{selectedModel.max_batch_weight_lb} lbs</span>
 </div>
 </div>
 </div>
 )}
 </div>

 {/* Food Items */}
 <div className="border-t pt-6">
 <div className="flex items-center justify-between mb-4">
 <div className="flex items-center gap-3">
 <div className="h-11 w-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
 <Package className="w-5 h-5" />
 </div>
 <div>
 <div className="font-heading text-xs font-extrabold uppercase tracking-[0.14em] text-foreground">
 Food items
 </div>
 <div className="text-sm text-muted-foreground">
 {items.length} {items.length === 1 ? 'item' : 'items'} added to this batch
 </div>
 </div>
 </div>
 <Button
 onClick={addItem}
 size="sm"
 className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
 >
 <Plus className="w-4 h-4 mr-2" />
 Add Item
 </Button>
 </div>

 <div className="space-y-4">
 {items.map((item, index) => {
 const itemIssues = itemValidation[item.id] || [];
 return (
 <div
 key={item.id}
 className={`p-4 border-2 rounded-lg ${
 itemIssues.length
 ? 'border-primary/50 bg-primary/5'
 : 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800'
 }`}
 data-testid={`household-item-card-${index}`}
 >
 <div className="flex items-center justify-between mb-3">
 <h4 className="font-semibold">Item {index + 1}</h4>
 <Button
 variant="ghost"
 size="sm"
 onClick={() => removeItem(item.id)}
 className="text-red-600 hover:text-red-700"
 >
 <Trash2 className="w-4 h-4" />
 </Button>
 </div>
 {itemIssues.length > 0 && (
 <div className="mb-3 p-3 rounded-lg bg-primary/10 border border-primary/30" data-testid={`household-item-warning-${index}`}>
 <p className="text-xs text-foreground/80">
 <strong className="text-primary">Needs your attention:</strong>{' '}
 Missing {itemIssues.join(', ')}. We used sensible defaults — fill these in for accurate numbers.
 </p>
 </div>
 )}

 <div className="grid md:grid-cols-2 gap-3">
 <div className="space-y-2">
 <Label>Food Name <span className="text-red-500">*</span></Label>
 <Input
 value={item.food_name}
 onChange={(e) => updateItem(item.id, 'food_name', e.target.value)}
 placeholder="e.g., Strawberries"
 />
 </div>

 <div className="space-y-2 md:col-span-2">
 <Label>Moisture Source <span className="text-red-500">*</span></Label>
 
 {/* Either/Or Selection */}
 <div className="grid md:grid-cols-2 gap-3">
 {/* Option 1: Category-Based */}
 <div className={`p-3 border-2 rounded-lg transition-all ${
 !item.custom_moisture 
 ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
 : 'border-slate-300 dark:border-slate-600'
 }`}>
 <div className="flex items-center gap-2 mb-3">
 <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
 !item.custom_moisture 
 ? 'border-blue-500 bg-blue-500' 
 : 'border-slate-300'
 }`}>
 {!item.custom_moisture && <div className="w-2 h-2 bg-white rounded-full"></div>}
 </div>
 <span className="text-sm font-semibold">Use Category Default</span>
 </div>
 
 {/* Native Radio Buttons for Categories */}
 <div className="space-y-2">
 {Object.entries(foodCategories).length === 0 && (
 <p className="text-xs text-red-600">Loading categories...</p>
 )}
 {Object.entries(foodCategories).map(([key, cat]) => (
 <label 
 key={key} 
 className="flex items-center space-x-2 cursor-pointer hover:bg-white dark:hover:bg-slate-700 p-2 rounded"
 >
 <input
 type="radio"
 name={`category-${item.id}`}
 value={key}
 checked={item.category === key}
 onChange={(e) => {
 console.log('Radio clicked:', e.target.value, 'Current:', item.category);
 const newCategory = e.target.value;
 setItems(items.map(i => 
 i.id === item.id 
 ? { ...i, category: newCategory, custom_moisture: '' }
 : i
 ));
 }}
 className="w-4 h-4 text-primary cursor-pointer"
 />
 <span className="text-sm flex-1 flex items-center justify-between">
 <span>{cat.name}</span>
 <span className="text-xs text-muted-foreground font-semibold">{cat.moisture_percent}%</span>
 </span>
 </label>
 ))}
 </div>
 
 {!item.custom_moisture && (
 <div className="mt-3 p-2 bg-blue-100 dark:bg-blue-900/30 rounded">
 <p className="text-xs text-blue-700 dark:text-blue-300 font-semibold">
 ✓ Using {foodCategories[item.category]?.name}: {foodCategories[item.category]?.moisture_percent}% moisture
 </p>
 </div>
 )}
 </div>
 
 {/* Option 2: AI Estimate */}
 <div className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
 item.custom_moisture 
 ? 'border-primary/30 bg-primary/5 dark:bg-navy/20' 
 : 'border-slate-300 dark:border-slate-600 hover:border-slate-400'
 }`}
 onClick={() => {
 if (!item.custom_moisture && item.food_name) {
 askAIForMoisture(item.id);
 }
 }}
 >
 <div className="flex items-center gap-2 mb-2">
 <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
 item.custom_moisture 
 ? 'border-primary/30 bg-primary/50' 
 : 'border-slate-300'
 }`}>
 {item.custom_moisture && <div className="w-2 h-2 bg-white rounded-full"></div>}
 </div>
 <span className="text-sm font-semibold">Ask AI for Specific Product</span>
 </div>
 <Button
 size="sm"
 variant="outline"
 onClick={(e) => {
 e.stopPropagation();
 askAIForMoisture(item.id);
 }}
 disabled={aiLoading || !item.food_name}
 className="w-full text-primary border-primary/20 hover:bg-primary/5 dark:text-primary"
 >
 <Sparkles className="w-4 h-4 mr-2" />
 {aiLoading ? 'Estimating...' : item.custom_moisture ? 'Re-estimate' : 'Get AI Estimate'}
 </Button>
 {item.custom_moisture && (
 <div className="mt-2">
 <Input
 type="number"
 step="1"
 value={item.custom_moisture}
 onChange={(e) => updateItem(item.id, 'custom_moisture', e.target.value)}
 className="w-full"
 placeholder="85"
 onClick={(e) => e.stopPropagation()}
 />
 <p className="text-xs text-primary dark:text-primary mt-1">
 ✓ Using {item.custom_moisture}% (AI estimate)
 </p>
 </div>
 )}
 </div>
 </div>
 
 {/* Prominent Moisture Display */}
 <div className="p-3 bg-cyan-50 dark:bg-cyan-900/20 rounded-lg border-2 border-cyan-300 dark:border-cyan-700">
 <div className="flex items-center gap-2 mb-2">
 <div className="w-2 h-2 bg-cyan-500 rounded-full"></div>
 <span className="text-xs font-bold text-cyan-900 dark:text-cyan-100">
 💧 Moisture Content: {item.custom_moisture || foodCategories[item.category]?.moisture_percent || '85'}%
 </span>
 </div>
 <p className="text-xs text-secondary dark:text-cyan-300">
 This food contains <strong>{item.custom_moisture || foodCategories[item.category]?.moisture_percent || '85'}% water</strong>. After freeze-drying: <strong>{100 - (parseFloat(item.custom_moisture) || foodCategories[item.category]?.moisture_percent || 85)}% solids</strong> remain
 </p>
 {item.custom_moisture && (
 <p className="text-xs text-amber-700 dark:text-amber-300 mt-2 italic">
 ⚠️ AI estimate - verify for accuracy. Category default is {foodCategories[item.category]?.moisture_percent}%
 </p>
 )}
 </div>
 </div>

 {/* Cycle Time & Electricity Rate - Per Item */}
 <div className="space-y-2">
 <div className="flex items-center justify-between mb-1">
 <Label>Cycle Time (hours) <span className="text-red-500">*</span></Label>
 <Button
 size="sm"
 variant="ghost"
 onClick={() => askAIForCycleTime(item.id)}
 disabled={aiLoading || !item.food_name || !selectedModel}
 className="h-6 text-xs text-primary hover:text-primary hover:bg-primary/5 dark:text-primary"
 >
 <Sparkles className="w-3 h-3 mr-1" />
 Ask AI
 </Button>
 </div>
 <p className="text-xs text-muted-foreground mb-2">⏱️ Time to fully freeze-dry this food</p>
 <Input
 type="number"
 step="0.5"
 value={item.cycle_time_hours}
 onChange={(e) => updateItem(item.id, 'cycle_time_hours', e.target.value)}
 placeholder="24"
 />
 {aiLoading && (
 <p className="text-xs text-amber-600 dark:text-amber-400 italic">
 ⚠️ AI estimates should be verified with your machine&apos;s manual.
 </p>
 )}
 </div>

 <div className="space-y-2">
 <Label>Electricity Rate ($/kWh)</Label>
 <p className="text-xs text-muted-foreground mb-2">💡 Find on your utility bill (average US: $0.12)</p>
 <Input
 type="number"
 step="0.01"
 value={item.electricity_rate}
 onChange={(e) => updateItem(item.id, 'electricity_rate', e.target.value)}
 placeholder="0.12"
 />
 </div>

 <div className="space-y-2 md:col-span-2">
 <div className="flex items-center justify-between">
 <Label>Wet Weight <span className="text-red-500">*</span></Label>
 <span className="text-xs text-muted-foreground">💡 Total fresh food weight</span>
 </div>
 <div className="flex gap-2">
 <Input
 type="number"
 step="0.1"
 value={item.wet_weight}
 onChange={(e) => updateItem(item.id, 'wet_weight', e.target.value)}
 placeholder="5"
 className="flex-1"
 />
 <select
 value={item.weight_unit}
 onChange={(e) => updateItem(item.id, 'weight_unit', e.target.value)}
 className="w-20 px-2 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-sm"
 >
 <option value="lbs">lbs</option>
 <option value="kg">kg</option>
 <option value="g">g</option>
 <option value="oz">oz</option>
 </select>
 </div>
 
 {/* Wet to Dry Weight Conversion Helper */}
 {item.wet_weight && calculateExpectedDryWeight(item) && (
 <div className="p-3 bg-pro-soft dark:from-blue-900/20 dark:to-cyan-900/20 rounded-lg border-2 border-blue-200 dark:border-blue-700">
 <div className="flex items-start gap-3">
 <div className="flex-1">
 <div className="flex items-center gap-2 mb-2">
 <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
 <p className="text-xs font-semibold text-blue-900 dark:text-blue-100">
 Expected After Freeze-Drying:
 </p>
 </div>
 
 <div className="space-y-1">
 <div className="flex items-center justify-between">
 <span className="text-xs text-blue-700 dark:text-blue-300">Dry Weight:</span>
 <span className="text-sm font-bold text-primary dark:text-blue-400">
 {calculateExpectedDryWeight(item).dryWeight} {calculateExpectedDryWeight(item).unit}
 </span>
 </div>
 
 <div className="flex items-center justify-between">
 <span className="text-xs text-blue-700 dark:text-blue-300">Water Removed:</span>
 <span className="text-xs font-semibold text-secondary dark:text-cyan-400">
 ~{calculateExpectedDryWeight(item).waterRemoved} {calculateExpectedDryWeight(item).unit} ({calculateExpectedDryWeight(item).moisturePercent}% moisture)
 </span>
 </div>
 
 <div className="mt-2 pt-2 border-t border-blue-200 dark:border-blue-700">
 <div className="flex items-center gap-2">
 <div className="flex-1 bg-white dark:bg-slate-800 rounded-full h-2 overflow-hidden">
 <div 
 className="h-full bg-primary text-primary-foreground"
 style={{ width: `${100 - calculateExpectedDryWeight(item).moisturePercent}%` }}
 ></div>
 </div>
 <span className="text-xs font-bold text-primary dark:text-blue-400">
 {(100 - calculateExpectedDryWeight(item).moisturePercent).toFixed(0)}% remains
 </span>
 </div>
 </div>
 </div>
 </div>
 </div>
 </div>
 )}
 
 {/* Per-Item Weight Warning for Selected Machine */}
 {selectedModel && item.wet_weight && (
 (() => {
 const itemWeightLbs = convertToGrams(item.wet_weight, item.weight_unit) / 453.592;
 const maxLbs = selectedModel.max_batch_weight_lb;
 const percentOfCapacity = (itemWeightLbs / maxLbs * 100).toFixed(0);
 const isOverCapacity = itemWeightLbs > maxLbs;
 
 return (
 <div className={`p-2 rounded border-2 ${
 isOverCapacity 
 ? 'bg-red-50 border-red-300 dark:bg-red-900/20 dark:border-red-700' 
 : percentOfCapacity > 70 
 ? 'bg-amber-50 border-amber-300 dark:bg-amber-900/20 dark:border-amber-700'
 : 'bg-green-50 border-green-300 dark:bg-green-900/20 dark:border-green-700'
 }`}>
 <p className={`text-xs font-semibold ${
 isOverCapacity 
 ? 'text-red-900 dark:text-red-100' 
 : percentOfCapacity > 70
 ? 'text-amber-900 dark:text-amber-100'
 : 'text-green-900 dark:text-green-100'
 }`}>
 {isOverCapacity ? '⚠️ Over Capacity!' : percentOfCapacity > 70 ? '⚠️ High Load' : '✓ Within Capacity'}
 </p>
 <p className={`text-xs mt-1 ${
 isOverCapacity 
 ? 'text-red-700 dark:text-red-300' 
 : percentOfCapacity > 70
 ? 'text-amber-700 dark:text-amber-300'
 : 'text-green-700 dark:text-green-300'
 }`}>
 This item: {itemWeightLbs.toFixed(2)} lbs ({percentOfCapacity}% of {selectedModel.display_name}&apos;s {maxLbs} lb capacity)
 </p>
 </div>
 );
 })()
 )}
 </div>

 <div className="space-y-2">
 <Label>Packaging Type</Label>
 <select
 value={item.packaging_type}
 onChange={(e) => updateItem(item.id, 'packaging_type', e.target.value)}
 className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800"
 >
 {Object.entries(packagingTypes).map(([key, pkg]) => (
 <option key={key} value={key}>{pkg.name}</option>
 ))}
 </select>
 </div>

 <div className="space-y-2 md:col-span-2">
 <div className="flex items-center justify-between">
 <Label>Wet Weight Per Bag <span className="text-red-500">*</span></Label>
 <span className="text-xs text-muted-foreground">
 💡 Fresh food per bag (will shrink when dried)
 </span>
 </div>
 <div className="flex gap-2">
 <Input
 type="number"
 step="0.1"
 value={item.wet_weight_per_bag}
 onChange={(e) => updateItem(item.id, 'wet_weight_per_bag', e.target.value)}
 placeholder="200"
 className="flex-1"
 />
 <select
 value={item.bag_weight_unit}
 onChange={(e) => updateItem(item.id, 'bag_weight_unit', e.target.value)}
 className="w-20 px-2 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-sm"
 >
 <option value="g">g</option>
 <option value="oz">oz</option>
 <option value="lbs">lbs</option>
 </select>
 </div>
 
 {/* Show dry weight per bag calculation */}
 {item.wet_weight_per_bag && calculateDryWeightPerBag(item) && (
 <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded border border-blue-300 dark:border-blue-700">
 <p className="text-xs text-blue-700 dark:text-blue-300">
 After freeze-drying: <strong>{calculateDryWeightPerBag(item).dryWeightG.toFixed(0)}g</strong> ({calculateDryWeightPerBag(item).dryWeightOz} oz) dry per bag
 </p>
 </div>
 )}
 
 {/* Bag count calculation */}
 {item.wet_weight_per_bag && item.wet_weight && (
 <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-300 dark:border-green-700">
 <div className="flex items-center justify-between">
 <span className="text-xs text-green-700 dark:text-green-300">
 📦 You&apos;ll get approximately:
 </span>
 <span className="text-sm font-bold text-green-600 dark:text-green-400">
 {calculateServings(item)} bags
 </span>
 </div>
 <p className="text-xs text-green-600 dark:text-green-400 mt-1">
 Each bag starts with {item.wet_weight_per_bag}{item.bag_weight_unit} wet {item.food_name || 'food'}
 </p>
 </div>
 )}
 </div>

 <div className="space-y-2 md:col-span-2">
 <Label>Ingredient Cost</Label>
 
 <div className="grid md:grid-cols-2 gap-3">
 {/* Cost Unit Selection */}
 <div className="space-y-2">
 <Label className="text-xs text-muted-foreground">Cost Unit</Label>
 <div className="flex gap-2">
 <select
 value={item.cost_unit}
 onChange={(e) => updateItem(item.id, 'cost_unit', e.target.value)}
 className="flex-1 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-sm"
 >
 <option value="lbs">$/lb</option>
 <option value="oz">$/oz</option>
 <option value="kg">$/kg</option>
 <option value="g">$/g</option>
 </select>
 </div>
 </div>
 
 {/* Cost Input */}
 <div className="space-y-2">
 <Label className="text-xs text-muted-foreground">Cost ${item.cost_unit === 'lbs' ? '/lb' : item.cost_unit === 'oz' ? '/oz' : item.cost_unit === 'kg' ? '/kg' : '/g'}</Label>
 <Input
 type="number"
 step="0.01"
 value={item.cost_per_unit}
 onChange={(e) => updateItem(item.id, 'cost_per_unit', e.target.value)}
 placeholder="7.79"
 className="w-full"
 />
 </div>
 </div>
 
 {/* Calculation Display */}
 {item.cost_per_unit && item.wet_weight && (
 <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border-2 border-blue-300 dark:border-blue-700">
 <div className="flex items-start gap-2 mb-2">
 <Lightbulb className="w-4 h-4 text-primary mt-0.5" />
 <div className="flex-1">
 <p className="text-xs font-semibold text-blue-900 dark:text-blue-100 mb-1">
 Calculation:
 </p>
 <p className="text-xs text-blue-700 dark:text-blue-300 font-mono">
 {convertToGrams(item.wet_weight, item.weight_unit) / (item.weight_unit === 'lbs' ? 453.592 : item.weight_unit === 'kg' ? 1000 : item.weight_unit === 'oz' ? 28.3495 : 1).toFixed(2)} {item.cost_unit} × ${parseFloat(item.cost_per_unit).toFixed(2)}/{item.cost_unit} = ${calculateItemCost(item)}
 </p>
 </div>
 </div>
 <div className="flex items-center justify-between pt-2 border-t border-blue-200 dark:border-blue-700">
 <span className="text-xs font-semibold text-blue-800 dark:text-blue-200">
 Total Ingredient Cost:
 </span>
 <span className="text-base font-bold text-primary dark:text-blue-400">
 ${calculateItemCost(item)}
 </span>
 </div>
 </div>
 )}
 </div>
 </div>
 </div>
 );
 })}

 {items.length === 0 && (
 <div className="text-center py-8 text-muted-foreground">
 <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
 <p>No items added yet. Click &ldquo;Add Item&rdquo; to get started!</p>
 </div>
 )}
 </div>
 </div>
 </CardContent>
 )}
 </Card>

 {/* Batch Capacity Monitor */}
 {items.length > 0 && selectedModel && weightCheck && (
 <Card className="mb-6 border-2 border-blue-300 dark:border-blue-700">
 <CardHeader>
 <CardTitle className="text-lg flex items-center gap-2">
 <TrendingUp className="w-5 h-5" />
 Batch Capacity Monitor
 </CardTitle>
 <CardDescription>
 Real-time monitoring of your batch weight and water content
 </CardDescription>
 </CardHeader>
 <CardContent>
 <div className={`p-4 rounded-lg border-2 ${
 weightCheck.isOver
 ? 'bg-red-50 border-red-300 dark:bg-red-900/20 dark:border-red-700'
 : weightCheck.isUnder
 ? 'bg-amber-50 border-amber-300 dark:bg-amber-900/20 dark:border-amber-700'
 : 'bg-green-50 border-green-300 dark:bg-green-900/20 dark:border-green-700'
 }`}>
 <div className="flex items-start gap-2 mb-3">
 {weightCheck.isOver ? (
 <XCircle className="w-6 h-6 text-red-600 dark:text-red-400 flex-shrink-0" />
 ) : weightCheck.isUnder ? (
 <AlertCircle className="w-6 h-6 text-amber-600 dark:text-amber-400 flex-shrink-0" />
 ) : (
 <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400 flex-shrink-0" />
 )}
 <div className="flex-1">
 <p className={`text-base font-bold ${
 weightCheck.isOver
 ? 'text-red-900 dark:text-red-100'
 : weightCheck.isUnder
 ? 'text-amber-900 dark:text-amber-100'
 : 'text-green-900 dark:text-green-100'
 }`}>
 {weightCheck.isOver 
 ? '⚠️ Weight Limit Exceeded!' 
 : weightCheck.isUnder
 ? '💡 Under-Capacity (Consider Adding More)'
 : '✓ Optimal Capacity'}
 </p>
 <p className="text-sm text-muted-foreground mt-1">
 Total Batch Weight: <strong className="text-base">{weightCheck.currentWeight} lbs</strong> of {weightCheck.maxWeight} lbs capacity
 </p>
 </div>
 </div>
 
 {/* Weight Progress Bar */}
 <div className="mb-3">
 <div className="flex justify-between text-sm mb-2">
 <span className="font-semibold">Weight Capacity</span>
 <span className="font-bold text-base">{weightCheck.percentUsed}%</span>
 </div>
 <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-4 overflow-hidden shadow-inner">
 <div
 className={`h-full rounded-full transition-all duration-300 ${
 weightCheck.isOver
 ? 'bg-red-600 dark:bg-red-500'
 : weightCheck.isUnder
 ? 'bg-amber-500 dark:bg-amber-400'
 : 'bg-green-600 dark:bg-green-500'
 }`}
 style={{ width: `${Math.min(weightCheck.percentUsed, 100)}%` }}
 />
 </div>
 </div>
 
 {/* Water Content Bar */}
 {getTotalWaterContent() > 0 && (
 <div className="mb-3">
 <div className="flex justify-between text-sm mb-2">
 <span className="font-semibold">💧 Water Content</span>
 <span className="font-bold text-base">{getTotalWaterContent()} lbs</span>
 </div>
 <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-4 overflow-hidden shadow-inner">
 <div
 className="h-full rounded-full bg-cyan-500 dark:bg-cyan-400 transition-all duration-300"
 style={{ width: `${Math.min((parseFloat(getTotalWaterContent()) / parseFloat(getTotalBatchWeight())) * 100, 100)}%` }}
 />
 </div>
 <p className="text-xs text-muted-foreground mt-1">
 ~{((parseFloat(getTotalWaterContent()) / parseFloat(getTotalBatchWeight())) * 100).toFixed(0)}% of total batch weight is water
 </p>
 </div>
 )}
 
 {/* Messages */}
 {weightCheck.isOver && (
 <div className="mt-3 p-3 bg-red-100 dark:bg-red-900/30 rounded-lg">
 <p className="text-sm text-red-800 dark:text-red-200 font-medium">
 ⚠️ Reduce item weights by {(parseFloat(weightCheck.currentWeight) - parseFloat(weightCheck.maxWeight)).toFixed(2)} lbs or remove items
 </p>
 </div>
 )}
 {weightCheck.isUnder && !weightCheck.isOver && (
 <div className="mt-3 p-3 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
 <p className="text-sm text-amber-800 dark:text-amber-200 font-medium">
 💡 You have {weightCheck.remainingCapacity} lbs of unused capacity. Consider adding more items for efficiency!
 </p>
 </div>
 )}
 </div>
 </CardContent>
 </Card>
 )}

 {/* Batch-Level Packing Costs */}
 {items.length > 0 && (
 <Card className="mb-6 border-2 border-primary/20">
 <CardHeader>
 <CardTitle className="text-lg flex items-center gap-2">
 <Package className="w-5 h-5" />
 Batch Packing Costs
 </CardTitle>
 <CardDescription>
 Optional: Add costs for packaging materials used for the entire batch
 </CardDescription>
 </CardHeader>
 <CardContent>
 <div className="grid md:grid-cols-3 gap-4">
 <div className="space-y-2">
 <Label>Container Cost ($)</Label>
 <Input
 type="number"
 step="0.01"
 value={batchPackingCosts.container_cost}
 onChange={(e) => setBatchPackingCosts({...batchPackingCosts, container_cost: e.target.value})}
 placeholder="0.50"
 />
 <p className="text-xs text-muted-foreground">💰 Mylar bags, jars, etc.</p>
 </div>
 
 <div className="space-y-2">
 <Label>O2 Absorber Cost ($)</Label>
 <Input
 type="number"
 step="0.01"
 value={batchPackingCosts.o2_absorber_cost}
 onChange={(e) => setBatchPackingCosts({...batchPackingCosts, o2_absorber_cost: e.target.value})}
 placeholder="0.25"
 />
 <p className="text-xs text-muted-foreground">💨 Oxygen absorbers</p>
 </div>
 
 <div className="space-y-2">
 <Label>Cleaning Cost ($)</Label>
 <Input
 type="number"
 step="0.01"
 value={batchPackingCosts.cleaning_cost}
 onChange={(e) => setBatchPackingCosts({...batchPackingCosts, cleaning_cost: e.target.value})}
 placeholder="0.10"
 />
 <p className="text-xs text-muted-foreground">🧼 Cleaning supplies</p>
 </div>
 </div>
 
 {(batchPackingCosts.container_cost || batchPackingCosts.o2_absorber_cost || batchPackingCosts.cleaning_cost) && (
 <div className="mt-4 p-3 bg-primary/5 dark:bg-navy/20 rounded-lg border-2 border-primary/20">
 <div className="flex items-center justify-between">
 <span className="text-sm font-semibold text-primary dark:text-primary">
 Total Batch Packing Cost:
 </span>
 <span className="text-lg font-bold text-primary dark:text-primary">
 ${(
 (parseFloat(batchPackingCosts.container_cost) || 0) + 
 (parseFloat(batchPackingCosts.o2_absorber_cost) || 0) + 
 (parseFloat(batchPackingCosts.cleaning_cost) || 0)
 ).toFixed(2)}
 </span>
 </div>
 <p className="text-xs text-primary dark:text-primary mt-2">
 This cost applies to the entire batch and will be distributed across all items.
 </p>
 </div>
 )}
 </CardContent>
 </Card>
 )}

 {/* Action Buttons */}
 <div className="grid md:grid-cols-2 gap-4 mb-6">
 <Button
 onClick={calculate}
 disabled={loading || items.length === 0 || !selectedModel}
 className="bg-primary text-primary-foreground text-lg py-6"
 >
 {loading ? (
 <>
 <Calculator className="w-5 h-5 mr-2 animate-spin" />
 Calculating...
 </>
 ) : (
 <>
 <Calculator className="w-5 h-5 mr-2" />
 Calculate Batch
 </>
 )}
 </Button>
 
 <Button
 onClick={() => {
 if (!outputs) {
 toast.error('Please calculate batch first');
 return;
 }
 generateBatchSummary();
 // Scroll to AI analysis section after a short delay
 setTimeout(() => {
 document.getElementById('ai-analysis-section')?.scrollIntoView({ 
 behavior: 'smooth',
 block: 'start'
 });
 }, 500);
 }}
 disabled={aiLoading || !outputs || items.length === 0}
 className="bg-primary text-primary-foreground hover:bg-primary/90 text-lg py-6"
 >
 {aiLoading ? (
 <>
 <Sparkles className="w-5 h-5 mr-2 animate-spin" />
 Generating AI Analysis...
 </>
 ) : (
 <>
 <Sparkles className="w-5 h-5 mr-2" />
 AI Batch Analysis
 </>
 )}
 </Button>
 <div className="mt-2 flex justify-center">
 <AICostBadge tool="calculator.ai_analysis" />
 </div>
 </div>

 {/* Results */}
 {outputs && (
 <div id="results-section">
 {/* Family B — headline metric snapshot */}
 <div className="mb-6" data-testid="household-family-b-metric">
 <HeadlineMetric
 label={batchName || 'Household batch'}
 value={`$${(outputs.total_batch_cost || 0).toFixed(2)}`}
 valueSuffix="/ batch"
 hint="Projected total cost for this batch based on your inputs — energy + consumables."
 sideMetrics={[
 { label: 'Wet weight', value: `${convertFromGrams(outputs.total_wet_weight_g, items[0]?.weight_unit || 'g')} ${items[0]?.weight_unit || 'g'}` },
 { label: 'Dry weight', value: `${convertFromGrams(outputs.total_dry_weight_g, items[0]?.weight_unit || 'g')} ${items[0]?.weight_unit || 'g'}` },
 { label: 'Water out', value: `${convertFromGrams(outputs.total_water_removed_g, items[0]?.weight_unit || 'g')} ${items[0]?.weight_unit || 'g'}` },
 { label: 'Energy', value: `${(outputs.total_energy_kwh || 0).toFixed(1)} kWh` },
 ]}
 testId="household-headline-metric"
 />
 </div>

 <Card className="mb-6 border-2 border-green-300 dark:border-green-700">
 <CardHeader
 className="cursor-pointer"
 onClick={() => setShowResults(!showResults)}
 >
 <div className="flex items-center justify-between">
 <div>
 <CardTitle className="text-2xl text-green-900 dark:text-green-100">
 ✅ Results
 </CardTitle>
 <CardDescription>Your batch calculations and savings</CardDescription>
 </div>
 {showResults ? <ChevronUp /> : <ChevronDown />}
 </div>
 </CardHeader>
 {showResults && (
 <CardContent>
 {/* Overload Error State */}
 {checkWeightCapacity()?.isOver && (
 <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border-2 border-red-500 dark:border-red-700 rounded-lg">
 <div className="flex items-start gap-3">
 <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
 <div>
 <h4 className="font-bold text-red-900 dark:text-red-100 mb-1">
 ⚠️ Machine Overloaded!
 </h4>
 <p className="text-sm text-red-800 dark:text-red-200">
 Your batch weighs <strong>{checkWeightCapacity()?.currentWeight} lbs</strong> but your {selectedModel?.display_name} can only handle <strong>{checkWeightCapacity()?.maxWeight} lbs</strong>. This may significantly increase cycle time, cause incomplete drying, and void your warranty.
 </p>
 <p className="text-xs text-red-700 dark:text-red-300 mt-2 font-semibold">
 Please reduce weight before proceeding.
 </p>
 </div>
 </div>
 </div>
 )}
 
 <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
 {/* Weight Summary */}
 <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-300 dark:border-blue-700">
 <p className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-1">Total Wet Weight</p>
 <p className="text-2xl font-bold text-primary">
 {convertFromGrams(outputs.total_wet_weight_g, items[0]?.weight_unit || 'g')} {items[0]?.weight_unit || 'g'}
 </p>
 <p className="text-xs text-muted-foreground mt-1">
 {outputs.total_wet_weight_g.toFixed(0)}g total
 </p>
 </div>
 
 {/* Dry Weight Summary */}
 <div className="p-4 bg-cyan-50 dark:bg-cyan-900/20 rounded-lg border border-cyan-300 dark:border-cyan-700">
 <p className="text-sm font-medium text-cyan-900 dark:text-cyan-100 mb-1">Total Expected Dry Weight</p>
 <p className="text-2xl font-bold text-secondary">
 {convertFromGrams(outputs.total_dry_weight_g, items[0]?.weight_unit || 'g')} {items[0]?.weight_unit || 'g'}
 </p>
 <p className="text-xs text-muted-foreground mt-1">
 After freeze-drying
 </p>
 </div>
 
 {/* Total Liquid Weight - Water Removed */}
 <div className="p-4 bg-sky-50 dark:bg-sky-900/20 rounded-lg border border-sky-300 dark:border-sky-700">
 <p className="text-sm font-medium text-sky-900 dark:text-sky-100 mb-1">Total Liquid Weight</p>
 <p className="text-2xl font-bold text-sky-600">
 {convertFromGrams(outputs.total_water_removed_g, items[0]?.weight_unit || 'g')} {items[0]?.weight_unit || 'g'}
 </p>
 <p className="text-xs text-muted-foreground mt-1">
 💧 Water to be removed
 </p>
 </div>

 {/* Energy Cost */}
 <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-300 dark:border-amber-700">
 <div className="flex items-center gap-2 mb-1">
 <Zap className="w-4 h-4 text-amber-600" />
 <p className="text-sm font-medium text-amber-900 dark:text-amber-100">Energy Cost</p>
 </div>
 <p className="text-2xl font-bold text-amber-600">${outputs.total_energy_cost.toFixed(2)}</p>
 <p className="text-xs text-muted-foreground mt-1">
 {outputs.total_energy_kwh.toFixed(1)} kWh used
 </p>
 </div>

 {/* Total Cost */}
 <div className="p-4 bg-primary/5 dark:bg-navy/20 rounded-lg border border-primary/20">
 <div className="flex items-center gap-2 mb-1">
 <DollarSign className="w-4 h-4 text-primary" />
 <p className="text-sm font-medium text-primary dark:text-primary">Total Batch Cost</p>
 </div>
 <p className="text-2xl font-bold text-primary">${outputs.total_batch_cost.toFixed(2)}</p>
 <p className="text-xs text-muted-foreground mt-1">
 ${outputs.cost_per_serving.toFixed(2)} per serving
 </p>
 </div>

 {/* Shelf Life */}
 <div className="p-4 bg-cyan-50 dark:bg-cyan-900/20 rounded-lg border border-cyan-300 dark:border-cyan-700">
 <div className="flex items-center gap-2 mb-1">
 <Clock className="w-4 h-4 text-secondary" />
 <p className="text-sm font-medium text-cyan-900 dark:text-cyan-100">Avg Shelf Life</p>
 </div>
 <p className="text-2xl font-bold text-secondary">{outputs.average_shelf_life_months.toFixed(0)} months</p>
 <p className="text-xs text-muted-foreground mt-1">
 {(outputs.average_shelf_life_months / 12).toFixed(1)} years
 </p>
 <p className="text-[11px] text-amber-700 dark:text-amber-300 mt-2 leading-snug" data-testid="shelf-life-safety-note">
 <strong>Safety note:</strong> always err on the side of the shortest shelf-life ingredient in your batch. For maximum food safety, plan storage and consumption around that lowest value — never the average.
 </p>
 </div>
 </div>

 {/* Cost Per Run Breakdown */}
 <div className="mt-6 p-6 bg-pro-soft rounded-lg border-2 border-primary/20">
 <div className="flex items-center gap-3 mb-5">
 <div className="h-11 w-11 rounded-xl bg-primary/15 text-primary flex items-center justify-center flex-shrink-0">
 <DollarSign className="w-5 h-5" />
 </div>
 <div>
 <div className="font-heading text-xs font-extrabold uppercase tracking-[0.14em] text-foreground">
 Cost per run breakdown
 </div>
 <div className="text-sm text-muted-foreground">
 Food, energy and packaging costs for this batch
 </div>
 </div>
 </div>
 
 <div className="space-y-3">
 {/* Food Cost */}
 <div className="flex items-center justify-between py-2 border-b border-primary/20">
 <span className="text-sm font-medium text-primary dark:text-primary">
 💰 Food Ingredients
 </span>
 <span className="text-sm font-bold text-primary dark:text-primary">
 ${outputs.total_food_cost.toFixed(2)}
 </span>
 </div>
 
 {/* Energy Cost */}
 <div className="flex items-center justify-between py-2 border-b border-primary/20">
 <div className="flex items-center gap-2">
 <Zap className="w-4 h-4 text-amber-600" />
 <span className="text-sm font-medium text-primary dark:text-primary">
 Electricity ({outputs.total_energy_kwh.toFixed(1)} kWh)
 </span>
 </div>
 <span className="text-sm font-bold text-primary dark:text-primary">
 ${outputs.total_energy_cost.toFixed(2)}
 </span>
 </div>
 
 {/* Packaging Cost (estimated) */}
 <div className="flex items-center justify-between py-2 border-b border-primary/20">
 <div className="flex items-center gap-2">
 <Package className="w-4 h-4 text-green-600" />
 <span className="text-sm font-medium text-primary dark:text-primary">
 Packaging (estimated)
 </span>
 </div>
 <span className="text-sm font-bold text-primary dark:text-primary">
 ${((outputs.total_batch_cost - outputs.total_food_cost - outputs.total_energy_cost) || 0).toFixed(2)}
 </span>
 </div>
 
 {/* Total */}
 <div className="flex items-center justify-between py-3 border-t-2 border-primary/20 dark:border-primary/30 mt-2">
 <span className="text-base font-bold text-primary dark:text-primary">
 💎 Total Cost Per Run
 </span>
 <span className="text-xl font-bold text-primary dark:text-primary">
 ${outputs.total_batch_cost.toFixed(2)}
 </span>
 </div>
 
 {/* Per Serving */}
 <div className="p-3 bg-white/50 dark:bg-slate-800/50 rounded-lg">
 <div className="flex items-center justify-between">
 <span className="text-sm text-primary dark:text-primary">
 Cost per serving/bag:
 </span>
 <span className="text-lg font-bold text-primary dark:text-primary">
 ${outputs.cost_per_serving.toFixed(2)}
 </span>
 </div>
 </div>
 </div>
 </div>

 {/* AI Batch Summary & Recommendations */}
 {batchSummary && (
 <div id="ai-analysis-section" className="mt-6 p-6 bg-pro-soft rounded-lg border-2 border-primary/20 scroll-mt-4">
 <div className="flex items-center gap-3 mb-5">
 <div className="h-11 w-11 rounded-xl bg-primary/15 text-primary flex items-center justify-center flex-shrink-0">
 <Sparkles className="w-5 h-5" />
 </div>
 <div>
 <div className="font-heading text-xs font-extrabold uppercase tracking-[0.14em] text-foreground">
 AI batch analysis
 </div>
 <div className="text-sm text-muted-foreground">
 Personalized recommendations from the academy assistant
 </div>
 </div>
 </div>
 <div className="bg-white dark:bg-slate-800 rounded-lg p-4 mb-4">
 {formatAIAnalysis(batchSummary)}
 </div>
 <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded border border-amber-300 dark:border-amber-700">
 <p className="text-xs text-amber-800 dark:text-amber-200 italic">
 ⚠️ AI-generated recommendations. Always verify food safety guidelines and machine capacity before adding items.
 </p>
 </div>
 </div>
 )}

 {/* Action Buttons */}
 <div className="grid md:grid-cols-3 gap-3 mt-6">
 <Button
 onClick={saveBatch}
 className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
 >
 <Save className="w-4 h-4 mr-2" />
 Save Batch
 </Button>
 <Button
 onClick={() => window.print()}
 variant="outline"
 className="border-blue-500 text-blue-700 hover:bg-blue-50 dark:border-blue-600 dark:text-blue-400"
 >
 <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
 </svg>
 Print
 </Button>
 <Button
 onClick={() => {
 const csvContent = `Batch Name,${batchName}\nTotal Wet Weight,${outputs.total_wet_weight_g}g\nTotal Dry Weight,${outputs.total_dry_weight_g}g\nEnergy Cost,$${outputs.total_energy_cost}\nTotal Cost,$${outputs.total_batch_cost}\nSavings,$${outputs.total_savings}\nShelf Life,${outputs.average_shelf_life_months} months`;
 const blob = new Blob([csvContent], { type: 'text/csv' });
 const url = URL.createObjectURL(blob);
 const a = document.createElement('a');
 a.href = url;
 a.download = `${batchName || 'batch'}.csv`;
 a.click();
 toast.success('Exported to CSV');
 }}
 variant="outline"
 className="border-primary/30 text-primary hover:bg-primary/5 dark:border-primary/30 dark:text-primary"
 >
 <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
 </svg>
 Export CSV
 </Button>
 </div>
 
 {/* Legal Disclaimer */}
 <div className="mt-6 p-4 bg-slate-100 dark:bg-slate-800 rounded-lg border border-slate-300 dark:border-slate-700">
 <h4 className="text-xs font-bold text-slate-900 dark:text-slate-100 mb-2">⚖️ Legal Disclaimer</h4>
 <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
 The calculations, recommendations, and AI-generated content provided by this calculator are for informational and educational purposes only. 
 Freeze Dry Academy makes no warranties, express or implied, regarding the accuracy, completeness, or reliability of this information. 
 Users are solely responsible for verifying all food safety guidelines, following manufacturer instructions for their specific freeze dryer model, 
 and ensuring compliance with local food safety regulations. Always consult your freeze dryer manual and relevant food safety authorities. 
 Freeze Dry Academy disclaims all liability for any damages, losses, or adverse outcomes resulting from the use of this calculator or reliance on its outputs.
 </p>
 </div>
 </CardContent>
 )}
 </Card>
 </div>
 )}
 
 {/* Load Batch Modal */}
 {showLoadModal && (
 <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
 <div className="bg-white dark:bg-slate-900 rounded-lg max-w-2xl w-full p-6 shadow-2xl max-h-[80vh] overflow-y-auto">
 <div className="flex items-center justify-between mb-4">
 <h3 className="text-lg font-bold text-slate-900 dark:text-white">
 📦 Load Saved Batch
 </h3>
 <Button
 variant="ghost"
 size="sm"
 onClick={() => setShowLoadModal(false)}
 className="text-slate-500 hover:text-slate-700"
 >
 ✕
 </Button>
 </div>
 
 {savedBatches.length === 0 ? (
 <div className="text-center py-8">
 <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
 <p className="text-slate-600 dark:text-slate-400">No saved batches found</p>
 <p className="text-sm text-slate-500 dark:text-slate-500 mt-1">
 Calculate and save a batch first to see it here
 </p>
 </div>
 ) : (
 <div className="space-y-3">
 {savedBatches.map((batch, index) => (
 <div
 key={index}
 className="p-4 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer"
 onClick={() => loadBatch(batch)}
 >
 <div className="flex items-center justify-between mb-2">
 <h4 className="font-semibold text-slate-900 dark:text-white">
 {batch.inputs.batch_name}
 </h4>
 <span className="text-xs text-slate-500 bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded">
 {batch.inputs.items.length} items
 </span>
 </div>
 <div className="grid grid-cols-2 gap-2 text-sm text-slate-600 dark:text-slate-400">
 <div>
 <span className="font-medium">Machine:</span> {batch.inputs.freeze_dryer_model_id}
 </div>
 <div>
 <span className="font-medium">Cycle:</span> {batch.inputs.cycle_time_hours}h
 </div>
 </div>
 <div className="mt-2 text-xs text-slate-500">
 Foods: {batch.inputs.items.map(item => item.food_name).join(', ')}
 </div>
 </div>
 ))}
 </div>
 )}
 
 <div className="flex justify-end mt-6">
 <Button
 onClick={() => setShowLoadModal(false)}
 variant="outline"
 >
 Cancel
 </Button>
 </div>
 </div>
 </div>
 )}

 {/* Load Batch Modal */}
 {showLoadModal && (
 <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
 <div className="bg-white dark:bg-slate-900 rounded-lg max-w-2xl w-full p-6 shadow-2xl max-h-[80vh] overflow-y-auto">
 <div className="flex items-center justify-between mb-4">
 <h3 className="text-lg font-bold text-slate-900 dark:text-white">
 📦 Load Saved Batch
 </h3>
 <Button
 variant="ghost"
 size="sm"
 onClick={() => setShowLoadModal(false)}
 className="text-slate-500 hover:text-slate-700"
 >
 ✕
 </Button>
 </div>
 
 {savedBatches.length === 0 ? (
 <div className="text-center py-8">
 <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
 <p className="text-slate-600 dark:text-slate-400">No saved batches found</p>
 <p className="text-sm text-slate-500 dark:text-slate-500 mt-1">
 Calculate and save a batch first to see it here
 </p>
 </div>
 ) : (
 <div className="space-y-3">
 {savedBatches.map((batch, index) => (
 <div
 key={index}
 className="p-4 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer transition-colors"
 onClick={() => loadBatch(batch)}
 >
 <div className="flex items-center justify-between mb-2">
 <h4 className="font-semibold text-slate-900 dark:text-white">
 {batch.inputs.batch_name}
 </h4>
 <span className="text-xs text-slate-500 bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded">
 {batch.inputs.items.length} items
 </span>
 </div>
 <div className="grid grid-cols-2 gap-2 text-sm text-slate-600 dark:text-slate-400">
 <div>
 <span className="font-medium">Cycle:</span> {batch.inputs.cycle_time_hours}h
 </div>
 <div>
 <span className="font-medium">Cost:</span> ${batch.outputs?.total_batch_cost?.toFixed(2) || 'N/A'}
 </div>
 </div>
 <div className="mt-2 text-xs text-slate-500">
 Foods: {batch.inputs.items.map(item => item.food_name).join(', ')}
 </div>
 </div>
 ))}
 </div>
 )}
 
 <div className="flex justify-end mt-6">
 <Button
 onClick={() => setShowLoadModal(false)}
 variant="outline"
 >
 Cancel
 </Button>
 </div>
 </div>
 </div>
 )}
 
 {/* Overload Warning Modal */}
 {showOverloadWarning && (
 <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
 <div className="bg-white dark:bg-slate-900 rounded-lg max-w-md w-full p-6 shadow-2xl">
 <div className="flex items-start gap-4 mb-4">
 <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center flex-shrink-0">
 <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
 </div>
 <div>
 <h3 className="text-lg font-bold text-red-900 dark:text-red-100 mb-2">
 ⚠️ Machine Overload Warning
 </h3>
 <p className="text-sm text-slate-700 dark:text-slate-300 mb-3">
 Your batch weighs <strong>{checkWeightCapacity()?.currentWeight} lbs</strong>, which exceeds your machine&apos;s capacity of <strong>{checkWeightCapacity()?.maxWeight} lbs</strong>.
 </p>
 <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded p-3 mb-3">
 <p className="text-xs text-amber-900 dark:text-amber-100 font-semibold mb-1">
 Risks of Overloading:
 </p>
 <ul className="text-xs text-amber-800 dark:text-amber-200 space-y-1 list-disc list-inside">
 <li>Significantly increased cycle time</li>
 <li>Incomplete freeze-drying</li>
 <li>Potential equipment damage</li>
 <li>May void machine warranty</li>
 </ul>
 </div>
 <p className="text-sm font-semibold text-slate-900 dark:text-white">
 Recommended: Reduce total weight by removing items or reducing portions.
 </p>
 </div>
 </div>
 <div className="flex gap-3">
 <Button
 onClick={() => setShowOverloadWarning(false)}
 className="flex-1 bg-slate-200 text-slate-900 hover:bg-slate-300 dark:bg-slate-700 dark:text-white dark:hover:bg-slate-600"
 >
 Go Back & Adjust
 </Button>
 <Button
 onClick={() => {
 setShowOverloadWarning(false);
 // Continue with calculation anyway (user accepts risk)
 calculate(true); // Pass true to bypass warning
 }}
 variant="outline"
 className="flex-1 border-2 border-red-500 text-red-700 hover:bg-red-50 dark:border-red-600 dark:text-red-400"
 >
 Proceed Anyway
 </Button>
 </div>
 </div>
 </div>
 )}
 
 {/* Advertisement Banner - Footer */}
 <div className="no-print mt-8 p-6 bg-pro-soft rounded-lg border-2 border-blue-300 dark:border-blue-700 shadow-lg">
 <div className="text-center mb-4">
 <h3 className="text-xl font-bold text-blue-900 dark:text-blue-100 mb-2">
 🌟 Join the Freeze Dry Academy Community
 </h3>
 <p className="text-sm text-blue-700 dark:text-blue-300">
 Connect with thousands of freeze-drying enthusiasts, share recipes, and get expert advice!
 </p>
 </div>
 <div className="grid md:grid-cols-3 gap-4">
 <div className="p-4 bg-white dark:bg-slate-800 rounded-lg shadow-sm">
 <div className="text-center">
 <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-2">
 <span className="text-xl">📚</span>
 </div>
 <p className="font-semibold text-sm text-slate-900 dark:text-background/80">Premium Recipes</p>
 <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">Curated batch recipes</p>
 </div>
 </div>
 <div className="p-4 bg-white dark:bg-slate-800 rounded-lg shadow-sm">
 <div className="text-center">
 <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-2">
 <span className="text-xl">👥</span>
 </div>
 <p className="font-semibold text-sm text-slate-900 dark:text-background/80">Expert Community</p>
 <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">Answers from real operators</p>
 </div>
 </div>
 <div className="p-4 bg-white dark:bg-slate-800 rounded-lg shadow-sm">
 <div className="text-center">
 <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-2">
 <span className="text-xl">🎬</span>
 </div>
 <p className="font-semibold text-sm text-slate-900 dark:text-background/80">Video Tutorials</p>
 <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">Step-by-step guides</p>
 </div>
 </div>
 </div>
 <div className="mt-4 text-center">
 <Button
 size="lg"
 className="bg-primary text-primary-foreground"
 onClick={() => window.open('https://freezedry.academy/community', '_blank')}
 >
 Join Now - It&apos;s Free! →
 </Button>
 </div>
 </div>
 </div>
 </div>
 </>
 );
};

export default HouseholdCalculator;
